import { useCommands, useHelpers } from '@remirror/react';
import { useCallback, useState, useEffect } from 'react';

const DEFAULT_FIND_STATE = {
  query: '',
  replacement: '',
  activeIndex: null,
  total: 0,
  caseSensitive: false,
};

function useFind() {
  const helpers = useHelpers();
  const commands = useCommands();
  const [state, setState] = useState(DEFAULT_FIND_STATE);

  const find = useCallback(
    (indexDiff = 0) => {
      setState((prev) => {
        const { query, activeIndex, caseSensitive } = prev;
        const result = helpers.findRanges({
          query,
          activeIndex: activeIndex === null ? 0 : activeIndex + indexDiff,
          caseSensitive,
        });
        return { ...prev, activeIndex: result.activeIndex ?? 0, total: result.ranges.length };
      });
    },
    [helpers]
  );

  const findNext = useCallback(() => find(+1), [find]);
  const findPrev = useCallback(() => find(-1), [find]);

  const stopFind = useCallback(() => {
    setState(DEFAULT_FIND_STATE);
    helpers.findRanges({ query: '', activeIndex: 0, caseSensitive: false });
    commands.stopFind();
  }, [helpers, commands, setState]);

  const replace = useCallback(() => {
    const { query, replacement, caseSensitive, activeIndex } = state;
    commands.findAndReplace({ query, replacement, caseSensitive, index: activeIndex ?? undefined });

    const isQuerySubsetOfReplacement = caseSensitive
      ? replacement.includes(query)
      : replacement.toLowerCase().includes(query.toLowerCase());

    if (isQuerySubsetOfReplacement) {
      findNext();
    } else {
      find();
    }
  }, [state, commands, findNext, find]);

  const replaceAll = useCallback(() => {
    const { query, replacement, caseSensitive } = state;
    commands.findAndReplaceAll({ query, replacement, caseSensitive });
    find();
  }, [commands, state, find]);

  const toggleCaseSensitive = useCallback(() => {
    setState((state) => ({ ...state, caseSensitive: !state.caseSensitive }));
  }, []);
  const setQuery = useCallback((query) => {
    setState((state) => ({ ...state, query }));
  }, []);
  const setReplacement = useCallback((replacement) => {
    setState((state) => ({ ...state, replacement }));
  }, []);

  useEffect(() => {
    find();
  }, [find, commands, state.query, state.caseSensitive]);

  return {
    ...state,
    findNext,
    findPrev,
    stopFind,
    replace,
    replaceAll,

    toggleCaseSensitive,
    setQuery,
    setReplacement,
  };
}

export default useFind;
